// Геометрия «полёта нагрева»: процент отрывается от открытого слова, летит по дуге к плитке бомбы
// (бомба «услышала» подсказку) и дальше — к бомбе на шкале. Чистые функции: DOM здесь не трогаем.

export interface Point {
  x: number;
  y: number;
}

export const dist = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Контрольная точка дуги между a и b: середина, сдвинутая вбок на треть длины (не меньше 28 px, не больше 90 px).
 * Сдвиг выбираем «вверх» (в сторону меньшего y), чтобы дуги выглядели как подброшенный мяч, а не как провисшая нить.
 */
export function arcControl(a: Point, b: Point): Point {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const len = dist(a, b) || 1;
  const shift = Math.min(90, Math.max(28, len / 3));
  // единичная нормаль к отрезку a→b
  let nx = -(b.y - a.y) / len;
  let ny = (b.x - a.x) / len;
  if (ny > 0 || (ny === 0 && nx > 0)) {
    nx = -nx;
    ny = -ny;
  }
  return { x: mid.x + nx * shift, y: mid.y + ny * shift };
}

const f = (n: number): string => n.toFixed(1);

/** Путь SVG/`offset-path` через точки: каждая пара соседних точек соединена квадратичной дугой. */
export function flightPath(points: readonly Point[]): string {
  const [first, ...rest] = points;
  if (!first) return '';
  let d = `M ${f(first.x)} ${f(first.y)}`;
  let prev = first;
  for (const p of rest) {
    const c = arcControl(prev, p);
    d += ` Q ${f(c.x)} ${f(c.y)} ${f(p.x)} ${f(p.y)}`;
    prev = p;
  }
  return d;
}

/**
 * В какой доле пути (0..1) полёт проходит промежуточную точку (плитку бомбы): пропорционально длине звеньев.
 * Нужна, чтобы «удар» по плитке бомбы совпал с моментом, когда процент до неё долетел.
 */
export function passFraction(a: Point, via: Point, b: Point): number {
  const first = dist(a, via);
  const total = first + dist(via, b);
  return total === 0 ? 0.5 : first / total;
}

/** Поддерживает ли браузер полёт вдоль пути (CSS Motion Path + Web Animations). Иначе нагрев показываем без полёта. */
export function canFly(): boolean {
  try {
    // DOM-типов в этом файле нет намеренно: его импортируют и тесты в Node
    const g = globalThis as unknown as {
      Element?: { prototype: { animate?: unknown } };
      CSS?: { supports?: (property: string, value: string) => boolean };
    };
    return (
      typeof g.Element?.prototype.animate === 'function' &&
      g.CSS?.supports?.('offset-path', 'path("M0 0 L1 1")') === true
    );
  } catch {
    return false;
  }
}
